import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import {
  AvatarDropdown,
  AvatarName,
  Footer,
  Question,
  SelectLang,
} from '@/components';
import { getPendingCertCount } from '@/pages/open-cert-audit/data';
import { getArrearsPausedCount } from '@/pages/trade-finance/arrears/data';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  openCertPendingCount?: number;
  arrearsPausedCount?: number;
}> {
  const readCurrentUserFromStorage = (): API.CurrentUser | undefined => {
    const rawUser = window.localStorage.getItem('currentUser');
    if (!rawUser) return undefined;
    try {
      return JSON.parse(rawUser) as API.CurrentUser;
    } catch {
      return undefined;
    }
  };

  const fetchUserInfo = async () => {
    return readCurrentUserFromStorage();
  };

  const fetchOpenCertPendingCount = async () => {
    try {
      const res = await getPendingCertCount();
      if (res.code === 200) {
        return res.data?.count ?? 0;
      }
    } catch {
      // 角标接口失败不影响登录态
    }
    return 0;
  };

  const fetchArrearsPausedCount = async () => {
    try {
      const res = await getArrearsPausedCount();
      if (res.code === 200) {
        return res.data?.count ?? 0;
      }
    } catch {
      // ignore
    }
    return 0;
  };

  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const [openCertPendingCount, arrearsPausedCount] = currentUser
      ? await Promise.all([
          fetchOpenCertPendingCount(),
          fetchArrearsPausedCount(),
        ])
      : [0, 0];
    return {
      fetchUserInfo,
      currentUser,
      openCertPendingCount,
      arrearsPausedCount,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    openCertPendingCount: 0,
    arrearsPausedCount: 0,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: false,
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => {
      const certCount = initialState?.openCertPendingCount ?? 0;
      const arrearsCount = initialState?.arrearsPausedCount ?? 0;
      const walk = (items: typeof menuData): typeof menuData =>
        items.map((item) => {
          const children = item.children ? walk(item.children) : undefined;
          if (item.path === '/open-cert-audit' && certCount > 0) {
            return {
              ...item,
              children,
              name: `${item.name} (${certCount})`,
            };
          }
          if (item.path === '/trade-finance/arrears' && arrearsCount > 0) {
            return {
              ...item,
              children,
              name: `${item.name} (${arrearsCount})`,
            };
          }
          return children ? { ...item, children } : item;
        });
      return walk(menuData);
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
// baseURL 配置：优先使用构建时注入的环境变量，否则回退到测试地址
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://test.api.sss.jsshuxintong.com';

export const request: RequestConfig = {
  baseURL: API_BASE_URL,
  ...errorConfig,
};
